/**
 * Shared singleton QueryClient — used by every monorepo app that consumes
 * React Query. Exported as a module-level singleton so auth handlers, error
 * boundaries, and other non-React contexts can call `queryClient.clear()` /
 * `invalidateQueries()` directly without needing a hook.
 *
 * Industry standard: TanStack Query docs, Linear, Vercel all export the
 * client as a module-level singleton rather than creating it inside the
 * component tree.
 *
 * Freshness strategy (per FE handbook 08-react-query-and-data-fetching.md):
 *  - staleTime 60s: queries are fresh for 60s after fetch (no redundant
 *    network hits when multiple components mount the same key).
 *  - gcTime 5min: cache survives 5 min after last consumer unmounts.
 *  - Dashboard surfaces (general-insights, monitor) override staleTime: 0
 *    at the hook level when they need always-fresh-on-mount semantics.
 *  - Every mutation calls queryClient.invalidateQueries() on success so
 *    stale data never lingers after a write.
 */
import { QueryClient, hashKey } from '@tanstack/react-query';
import { ApiError } from './apiClient';

// Current authenticated user UID — set by auth context on sign-in/sign-out.
// Module-level so it survives React re-renders and is accessible to the
// queryKeyHashFn without a hook (SEC-002).
let currentUid: string | null = null;

/** Call from AuthContext immediately after sign-in and on sign-out (SEC-002). */
export function setQueryClientUserId(uid: string | null): void {
  currentUid = uid;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Prefix every cache key hash with the authenticated user's UID so that
      // cross-user cache pollution cannot occur when two users share a browser
      // or when a user signs out and another signs in (SEC-002).
      queryKeyHashFn: (queryKey) => {
        const prefix = currentUid ?? '__anon__';
        return `${prefix}:${hashKey(queryKey)}`;
      },
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      // SSE is the push channel for data changes — window-focus polling causes
      // skeleton loading storms every time the user switches tabs. Specific
      // surfaces that need always-fresh-on-mount semantics (general-insights,
      // monitor) override this at the hook level with staleTime: 0.
      refetchOnWindowFocus: false,
      // SSE reconnect fires targeted invalidateQueries() on the affected keys
      // only — a blanket refetch on reconnect would hammer the API unnecessarily.
      refetchOnReconnect: false,
      // 4xx responses (401/403/404/422…) are terminal client errors — retrying
      // them is pointless: it just doubles the request and the console noise
      // (e.g. a 403 on /last-import-result for users without import visibility).
      // Retry once only for transient 5xx / network errors.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.httpStatus >= 400 && error.httpStatus < 500
          ? false
          : failureCount < 1,
    },
    mutations: { retry: false },
  },
});
